import {
  cloneElement,
  useId,
  useState,
  type FocusEvent,
  type MouseEvent,
  type ReactElement,
} from 'react';
import clsx from 'clsx';
import { Popover, PopoverAnchor, PopoverContent, PopoverPortal } from '@radix-ui/react-popover';

export const propertyActionIconClassName =
  'flex h-11 w-11 items-center justify-center rounded-full border border-surface-subtle bg-white text-brand-primary shadow-sm transition hover:border-brand-primary/60 hover:bg-brand-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40 disabled:cursor-not-allowed disabled:opacity-50';

interface PropertyActionIconButtonProps {
  label: string;
  children: ReactElement;
  side?: 'top' | 'right' | 'bottom' | 'left';
}

export function PropertyActionIconButton({
  label,
  children,
  side = 'top',
}: PropertyActionIconButtonProps): ReactElement {
  const [open, setOpen] = useState(false);
  const popoverId = `property-action-${useId()}`;
  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };
  const trigger = cloneElement(children, {
    'aria-haspopup': 'dialog',
    'aria-controls': popoverId,
    'aria-expanded': open,
    onMouseEnter: (event: MouseEvent<HTMLElement>) => {
      children.props.onMouseEnter?.(event);
      handleOpen();
    },
    onMouseLeave: (event: MouseEvent<HTMLElement>) => {
      children.props.onMouseLeave?.(event);
      handleClose();
    },
    onFocus: (event: FocusEvent<HTMLElement>) => {
      children.props.onFocus?.(event);
      handleOpen();
    },
    onBlur: (event: FocusEvent<HTMLElement>) => {
      children.props.onBlur?.(event);
      handleClose();
    },
  });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>{trigger}</PopoverAnchor>
      <PopoverPortal>
        <PopoverContent
          id={popoverId}
          side={side}
          align="center"
          sideOffset={8}
          collisionPadding={12}
          aria-label={label}
          className="rounded-md border border-brand-primary/20 bg-brand-primary px-3 py-1 text-xs font-medium text-white shadow-lg"
        >
          {label}
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  );
}

export function combineActionIconClassName(...classNames: Array<string | undefined>): string {
  return clsx(propertyActionIconClassName, classNames);
}
